import {Injectable, TemplateRef} from "@angular/core";
import {MatDialog, MatDialogConfig, MatDialogRef} from "@angular/material/dialog";
import {Observable, Subject, tap} from "rxjs";
import {AuthService} from "./auth.service";
import {CookieModalComponent} from "../features/cookie-modal/cookie-modal.component";
import {ExtraTermsModalComponent} from "../features/login/extra-terms-modal/extra-terms-modal.component";
import {TermsText} from "./contact-info.service";
import {ChartServiceData} from "./chart.service";
import {take} from "rxjs/operators";
import {SuccessModalComponent} from "../shared/success-modal/success-modal.component";
import {AppInstallBannerComponent} from "../shared/app-install-banner/app-install-banner.component";
import {VerificationModalComponent} from "../shared/verification-modal/verification-modal.component";
import {IdleModalComponent} from "../shared/idle-modal/idle-modal.component";
import {AreYouSureModalComponent} from "../shared/are-you-sure-modal/are-you-sure-modal.component";
import {GiveConsentModalComponent} from "../shared/give-consent-modal/give-consent-modal.component";
import {ChartModalComponent} from "../shared/charts/chart-modal/chart-modal.component";
import {WarningModalComponent} from "../features/settings/my-consents/warning-modal/warning-modal.component";
import {
  PrescriptionConsentModalComponent,
} from "../features/health-plan/prescription-consent-modal/prescription-consent-modal.component";
import {InfoModalComponent} from "../shared/info-modal/info-modal.component";
import {BottomSheetComponent, BottomSheetData} from "../shared/components/bottom-sheet/bottom-sheet.component";
import {MatBottomSheet} from "@angular/material/bottom-sheet";
import {DialogComponent} from "../shared/components/dialog/dialog.component";
import {DialogData} from "../models/dialog-data.model";
import {TranslateService} from "@ngx-translate/core";
import {
  SessionExpiredModalComponent,
} from "../shared/components/session-expired-modal/session-expired-modal.component";
import {PaymentModalComponent} from "../features/premium-services/payment-modal/payment-modal.component";
import {
  NewVersionDialogComponent
} from "../shared/components/new-version-dialog/new-version-dialog/new-version-dialog.component";
import {EmailValidationModalComponent} from "../shared/email-validation-modal/email-validation-modal.component";
import {Router} from "@angular/router";

@Injectable({ providedIn: "root" })
export class ModalService {
  modalClosed = new Subject<boolean>();
  modalCanceled = new Subject<boolean>();
  warningModalClosed = new Subject<string>();
  bannerClosed = new Subject<any>();
  isErrorModal = new Subject<boolean>();
  installBannerShowCount: number = 0;
  verifModalClosed = new Subject<boolean>();
  consentModalClosed = new Subject<{ termsText: TermsText, prescriptionConsent?: string }>();
  activeDialogRef: MatDialogRef<any>;
  private dialogRef: MatDialogRef<SuccessModalComponent>;
  public requestResponseSuccess = new Subject<boolean>()

  constructor(
    private dialog: MatDialog,
    private authService: AuthService,
    private bottomSheet: MatBottomSheet,
    private translate: TranslateService,
    private router: Router,
  ) {
  }


  // Displaying a modal with the provided configuration.
  showModal(message: {
    isProgress?: boolean;
    showErrorReport: boolean;
    isSuccess: boolean;
    title: string;
    errorCode?: string;
    description?: string;
  }) {

    // Create a dialog configuration based on the message configuration
    const dialogConfig = this.createSuccessDialogConfig(message);
    // Set the dialog to be unclosable during progress
    if (message.isProgress != null) dialogConfig.disableClose = message.isProgress;
    // Open the modal component with the dialog configuration
    this.dialogRef = this.dialog.open(SuccessModalComponent, dialogConfig);
    // Set the current active dialog to be this dialog
    this.activeDialogRef = this.dialogRef;
    // Subscribe to the afterClosed event of the dialog
    this.dialogRef.afterClosed().subscribe(() => {
      // Update the modalClosed subject with a value of true
      this.modalClosed.next(true);
    });
    // Return the dialog reference
    return this.dialogRef;
  }

  showInfoModal(modalTemplate: TemplateRef<any>) {
    const dialogConfig = this.createDialogConfig(modalTemplate);
    const dialogRef = this.dialog.open(InfoModalComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(() => {
      this.modalClosed.next(true);
    });
  }

  showInstallBannerOnMainPage() {
    if (this.installBannerShowCount < 1) {
      this.showInstallBanner();
      this.installBannerShowCount += 1;
    }
  }

  showInstallBanner() {
    const dialogConfig = this.createInstallBannerDialogConfig();
    const dialogRef = this.dialog.open(AppInstallBannerComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(() => {
      this.bannerClosed.next(null);
    });
  }

  modifyErrorTitleForTranslation(title: string) {
    const listOfErrorTitlesToModifyForTranslation: string[] = ["NT-002"];

    if (listOfErrorTitlesToModifyForTranslation.includes(title)) {
      return title.replace(/-/g, '_');
    } else {
      return title;
    }
  }

  showErrorModal(title: string, errorCode: string, showErrorReport: boolean) {
    title = this.modifyErrorTitleForTranslation(title);
    let modalRef = this.showModal({ isSuccess: false, showErrorReport: showErrorReport, title: title, errorCode: errorCode});

    //TODO: This part should be removed in future and handled in global error and exception handler service. From here...
    this.activeDialogRef = modalRef;
    if (modalRef) {
      modalRef.afterClosed().subscribe(() => {
        if (title.startsWith("NT-001") ||
          title.startsWith("NT_002") ||
          title.startsWith("NT-003")) {
          this.authService.logout();
        }
      });
    }
    if (
      title.startsWith("SYS-0000") ||
      title.startsWith("SES-111") ||
      title.startsWith("SES-009")
    ) {
      this.authService.logout();
    }
    // TODO: ... to there.

    this.isErrorModal.next(true);
  }

  showVerificationModal(
    verifCode: string,
    secondsToCountDown: number,
  ): MatDialogRef<VerificationModalComponent> {
    const dialogConfig = this.createDialogConfig({ verifCode, secondsToCountDown });
    const dialogRef = this.dialog.open(
      VerificationModalComponent,
      dialogConfig,
    );
    this.activeDialogRef = dialogRef;
    dialogRef.afterClosed().subscribe(() => {
      this.verifModalClosed.next(true);
    });
    return dialogRef;
  }

  showIdleModal(idleState: string) {
    const dialogConfig = this.createDialogConfig(idleState);
    const dialogRef = this.dialog.open(IdleModalComponent, dialogConfig);
    this.activeDialogRef = dialogRef;
    dialogRef.afterClosed().subscribe(() => {
      this.modalClosed.next(true);
    });
  }

  showSessionExpiredModal() {
    this.dialog.open(SessionExpiredModalComponent, {
      width: "300px",
      disableClose: true,
    });
  }

  showAreYouSureModal(extra?: string) {
    const dialogConfig = this.createDialogConfig();
    const dialogRef = this.dialog.open(AreYouSureModalComponent, dialogConfig);
    this.activeDialogRef = dialogRef;
    if (extra) {
      dialogRef.afterClosed().subscribe(() => {
        this.modalClosed.next(null);
      });
    }
    dialogRef.afterClosed().subscribe(() => {
      this.modalClosed.next(true);
    });
  }

  showGiveConsentModal(message: {
    isSuccess?: boolean;
    title?: string;
    description?: string;
  }) {
    const dialogConfig = this.createConsentDialogConfig(message);
    const dialogRef = this.dialog.open(
      GiveConsentModalComponent,
      dialogConfig,
    );
    this.activeDialogRef = dialogRef;
    dialogRef.afterClosed().subscribe(() => {
      this.modalClosed.next(true);
    });
  }

  createConsentDialogConfig(data?: any): MatDialogConfig {
    const dialogConfig = new MatDialogConfig();
    if (data) {
      dialogConfig.data = data;
    }
    dialogConfig.ariaLabel = "info";
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.panelClass = "custom-no-padding-dialog";
    dialogConfig.backdropClass = "custom-darker-modal-backdrop";
    return dialogConfig;
  }

  showWarningModal(content?: string, previousCheckboxState?: boolean, checkboxName?: string) {
    const dialogConfig = this.createDialogConfig({
      content: content,
      previousCheckboxState: previousCheckboxState,
      checkboxName: checkboxName,
    });
    const dialogRef = this.dialog.open(WarningModalComponent, dialogConfig);
    this.activeDialogRef = dialogRef;
    dialogRef.afterClosed()
      .subscribe((result: {
        closureType: "consentRemoved" | "cancelButtonPressed",
        accept: boolean,
        termsText: TermsText
      }) => {
        if (result.closureType === "cancelButtonPressed") {
          this.modalCanceled.next(previousCheckboxState);
        }
        if (result.closureType === "consentRemoved") {
          this.modalCanceled.next(false);
        }
        if (result.termsText !== undefined) {
          this.consentModalClosed.next({ termsText: result.termsText });
        }
        this.warningModalClosed.next(result.accept.toString());
      });
  }

  showCookieModal() {
    const dialogConfig = this.createCookieDialogConfig();
    this.dialog.open(CookieModalComponent, dialogConfig);
  }

  showExtraTermsModal(content: string, previousCheckboxState?: boolean) {
    const dialogConfig = this.createDialogConfig(content);
    dialogConfig.disableClose = true;
    const dialogRef = this.dialog.open(ExtraTermsModalComponent, dialogConfig);
    this.activeDialogRef = dialogRef;
    dialogRef.afterClosed().subscribe((isAccept: string) => {
      if (isAccept === "false") {
        this.modalClosed.next(false);
        this.showAreYouSureModal("extra");
      } else if (isAccept === "true") {
        this.modalClosed.next(true);
      } else this.modalClosed.next(previousCheckboxState);
    });
  }

  showPrescriptionConsentModal(disableClose: boolean, previousCheckboxState?: boolean) {
    const dialogConfig = this.createDialogConfig();
    dialogConfig.disableClose = disableClose;
    dialogConfig.data = { previousCheckboxState }; // Pass previousCheckboxState to modal
    const dialogRef = this.dialog.open(
      PrescriptionConsentModalComponent,
      dialogConfig,
    );
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (result.termsText !== undefined) {
          this.consentModalClosed.next({
            termsText: result.termsText,
            prescriptionConsent: result.prescriptionConsent,
          });
        }
      } else {
        this.modalCanceled.next(previousCheckboxState);
        this.consentModalClosed.next(null);
      }
    });
  }

  createDialogConfig(data?: any): MatDialogConfig {
    const dialogConfig = new MatDialogConfig();
    if (data) {
      dialogConfig.data = data;
    }
    dialogConfig.panelClass = "custom-no-padding-dialog";
    dialogConfig.backdropClass = "custom-darker-modal-backdrop";

    // Set ARIA attributes for accessibility
    dialogConfig.ariaLabel = "info";
    dialogConfig.ariaDescribedBy = "modal-description";
    dialogConfig.role = "alertdialog";
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    return dialogConfig;
  }

  createSuccessDialogConfig(data?: any): MatDialogConfig {
    const dialogConfig = new MatDialogConfig();
    if (data) {
      dialogConfig.data = data;
    }
    dialogConfig.panelClass = "custom-no-padding-dialog";
    dialogConfig.backdropClass = "custom-darker-modal-backdrop";

    // Set ARIA attributes for accessibility
    dialogConfig.autoFocus = false;
    dialogConfig.role = "alertdialog";
    return dialogConfig;
  }

  createInstallBannerDialogConfig() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = "custom-no-padding-dialog";
    dialogConfig.backdropClass = "custom-darker-modal-backdrop";
    dialogConfig.role = "alertdialog";
    dialogConfig.ariaLabelledBy = "install-app-title";
    dialogConfig.ariaDescribedBy = "install-app-description";
    return dialogConfig;
  }

  createCookieDialogConfig(): MatDialogConfig {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = "custom-no-padding-dialog";
    dialogConfig.backdropClass = "custom-cookie-backdrop";
    dialogConfig.closeOnNavigation = false;
    dialogConfig.disableClose = true;
    dialogConfig.ariaLabelledBy = "privacy-title";
    dialogConfig.ariaDescribedBy = "privacy-text";
    dialogConfig.autoFocus = true;
    dialogConfig.restoreFocus = true;
    return dialogConfig;
  }

  closeModal(dialogRef: any) {
    if (dialogRef) {
      dialogRef.close();
    }
  }

  closeActiveDialogRef() {
    if (this.activeDialogRef) {
      this.activeDialogRef.close();
    }
  }

  getModalClosed(): Observable<boolean> {
    return this.modalClosed.asObservable();
  }

  showChartModal(serviceData: ChartServiceData) {
    const dialogConfig = this.createDialogConfig({ serviceData });
    dialogConfig.panelClass = "chart-dialog";
    const dialogRef = this.dialog.open(ChartModalComponent, dialogConfig);
    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        this.modalClosed.next(true);
      });
  }

  openBottomSheet(data: BottomSheetData): void {
    this.bottomSheet.open(BottomSheetComponent, {
      panelClass: ["info-sheet", "vh-20"],
      data: data,
      ariaLabel: this.translate.instant("accessibility.infoModal"),
    });
  }

  openDialog(data: DialogData) {
    const dialogRef = this.dialog.open(DialogComponent, {
      data: data,
    });
    dialogRef.afterClosed().subscribe(() => {
      return this.authService.clearSession();
    });
  }

  async showPaymentModal(content: string): Promise<boolean> {
    const dialogConfig = this.createDialogConfig({ content });
    const dialogRef = this.dialog.open(PaymentModalComponent, dialogConfig);

    return new Promise<boolean>((resolve) => {
      dialogRef.componentInstance.goToPay.subscribe((accept: boolean) => {
        dialogRef.componentInstance.goToPay.unsubscribe();
        resolve(accept);
      });
    });
  }

  showNewVersionDialog(message: string) {
    this.dialog.open(NewVersionDialogComponent, {
      data: { message: message },
      width: '300px',
      position: { top: '20px', right: '20px' },
      hasBackdrop: false,
      disableClose: true,
      panelClass: 'custom-dialog-container',
    });
  }

  showEmailValidationModal(): MatDialogRef<EmailValidationModalComponent> {
    return this.dialog.open(EmailValidationModalComponent, {
      width: '400px',
    });
  }

  showModalsAfterSubmitting(sendingDescription: string, sentTitle: string, sentDescription: string, reroute?: boolean): Observable<any> {
    // Open 'sending' dialog modal
    const sendingDialog = this.showModal({
      isProgress: true,
      showErrorReport: false,
      isSuccess: false,
      title: "shared.messageSendingText",
      description: sendingDescription
    });

    // Return an observable for the caller to subscribe to
    return this.requestResponseSuccess
      .pipe(
        take(1),
        tap((response) => {
          // Close 'sending' modal
          this.closeModal(sendingDialog);

          // Only if the response is successful
          if (response === true) {
            // Open new modal to show success message
            const updatedModal = this.showModal({
              isProgress: false,
              showErrorReport: false,
              isSuccess: true,
              title: sentTitle,
              description: sentDescription
            });
            // disable rerouting for selected components
            if (reroute != false) {
              // Navigate to main menu after updated modal is closed
              updatedModal.afterClosed().subscribe(() => {
                this.router.navigate(["/"]);
              });
            }
          }

        })
      );
  }

}
